import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, AlertCircle, Target, TrendingUp, Award } from 'lucide-react';
import { INDUSTRIES } from '../data/industries';
import { PRODUCTS } from '../data/products';

const SITE_URL = 'https://loanyardcapital.com';

const scrollToContact = () => { window.location.href = '/#contact'; };

export default function IndustryPage() {
  const { slug } = useParams();
  const ind = INDUSTRIES[slug];

  useEffect(() => {
    if (!ind) return;
    window.scrollTo(0, 0);
    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: ind.faqs.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Industries', item: `${SITE_URL}/industries` },
          { '@type': 'ListItem', position: 3, name: ind.name, item: `${SITE_URL}/industries/${slug}` }
        ]
      }
    ];
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'industry-schema';
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { document.getElementById('industry-schema')?.remove(); };
  }, [ind, slug]);

  if (!ind) return <Navigate to="/" replace />;
  const cs = ind.caseStudy;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <WhatsAppButton />

      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-36 lg:pt-40 pb-12 sm:pb-16 bg-slate-950 text-white relative overflow-hidden" data-testid="industry-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.15),transparent_60%)]"></div>
        <div className="container mx-auto px-4 relative">
          <p className="text-emerald-400 text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] mb-3 sm:mb-4">
            <Link to="/" className="text-slate-400 hover:text-emerald-300">Home</Link> <span className="text-slate-600">/</span>{' '}
            <Link to="/industries" className="text-slate-400 hover:text-emerald-300">Industries</Link> <span className="text-slate-600">/</span> {ind.name}
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold leading-[1.1] sm:leading-[1.1] max-w-4xl mb-4 sm:mb-6 text-white">{ind.heroTitle}</h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-3xl leading-relaxed mb-6 sm:mb-8">{ind.heroSub}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button onClick={scrollToContact} size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 h-auto text-lg" data-testid="industry-cta-primary">
              Get a Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="https://wa.me/919892759155" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 py-6 h-auto text-lg w-full">
                WhatsApp an Expert
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">How We Serve the {ind.name} Sector</h2>
          {ind.intro.map((para, i) => (
            <p key={`intro-${i}`} className="text-lg text-slate-600 leading-relaxed mb-5">{para}</p>
          ))}
        </div>
      </section>

      {/* Challenges */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Funding Challenges We Solve</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {ind.challenges.map((c, i) => (
              <div key={`ch-${i}`} className="flex items-start gap-3 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <AlertCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 font-medium">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Capital Solutions for {ind.name}</h2>
          <p className="text-slate-600 text-center mb-10 max-w-3xl mx-auto">
            We offer comprehensive funding options tailored to the specific needs of the {ind.name} sector. From working capital to long-term growth financing, explore our specialized solutions below.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {ind.solutions.filter(([s]) => PRODUCTS[s]).map(([s, label]) => (
              <Link key={s} to={`/${s}`} className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all group" data-testid={`industry-solution-${s}`}>
                <p className="font-semibold text-slate-900 group-hover:text-emerald-700">{label}</p>
                {PRODUCTS[s]?.amount && (
                  <p className="text-xs text-slate-500 mt-1">{PRODUCTS[s].amount}</p>
                )}
                <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">Explore Solution <ArrowRight className="w-3 h-3" /></p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/#capital-solutions" className="text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-2">
              View All Capital Solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-16 bg-slate-950 text-white" data-testid="industry-case-study">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-[0.25em] mb-3 text-center">Case Study</p>
          <h2 className="text-3xl font-bold mb-2 text-center">{cs.client}</h2>
          <p className="text-emerald-300 text-center mb-12 font-medium">{cs.requirement}</p>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-7">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-lg">The Challenge</h3>
              </div>
              <p className="text-slate-300 leading-relaxed">{cs.challenge}</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-7">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-lg">Our Solution</h3>
              </div>
              <p className="text-slate-300 leading-relaxed">{cs.solution}</p>
            </div>
          </div>
          <div className="bg-emerald-600/10 border border-emerald-500/30 rounded-xl p-7">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-emerald-400" />
              <h3 className="font-bold text-lg">Business Impact</h3>
            </div>
            <ul className="grid md:grid-cols-3 gap-4">
              {cs.impact.map((im, i) => (
                <li key={`imp-${i}`} className="flex items-start gap-2 text-slate-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2.5 flex-shrink-0"></span>{im}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="industry-faq">
            {ind.faqs.map(([q, a], i) => (
              <AccordionItem key={`faq-${i}`} value={`faq-${i}`} className="bg-white border border-slate-200 rounded-xl px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-emerald-700 py-5">{q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-slate-950 text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">Discuss Your {ind.name} Funding Requirement</h2>
          <p className="text-slate-300 mb-8">Speak to a corporate finance specialist who understands your sector. Consultation is free and confidential.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={scrollToContact} size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 h-auto text-lg" data-testid="industry-cta-final">
              Apply Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:+919892759155">
              <Button size="lg" variant="outline" className="border border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 py-6 h-auto text-lg w-full">
                Call +91 98927 59155
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
